import React, { useState } from 'react';
import { Check, Copy, Download, Send } from 'lucide-react';
import {
  FORMATI,
  altBadge,
  codiceBadge,
  codiceLinkTestuale,
  fileBadge,
  mailtoWebmaster,
  type FamigliaBadge,
  type FormatoBadge,
  type TemaBadge
} from '../../utils/badgeAssociati';

interface Props {
  famiglia: FamigliaBadge;
  /** Scheda della mappa della Rete (obbligatoria per il badge della Rete). */
  idScheda?: string;
  /** Titolo della scheda: per la Rete il nome della sede. */
  nome?: string;
}

const TEMI: { valore: TemaBadge; etichetta: string }[] = [
  { valore: 'chiaro', etichetta: 'Chiaro' },
  { valore: 'scuro', etichetta: 'Scuro' }
];

const CARD = 'rounded-2xl bg-white dark:bg-gray-900 shadow-sm ring-1 ring-black/5 dark:ring-white/10';

/**
 * Un badge da inserire nel proprio sito: anteprima, scelta di formato e tema,
 * codice HTML da copiare, immagine da scaricare e link testuale.
 */
const SchedaBadge: React.FC<Props> = ({ famiglia, idScheda, nome }) => {
  const [formato, setFormato] = useState<FormatoBadge>('orizzontale');
  const [tema, setTema] = useState<TemaBadge>('chiaro');
  const [copiato, setCopiato] = useState(false);

  const codice = codiceBadge(famiglia, formato, tema, idScheda);
  const file = fileBadge(famiglia, formato, tema);
  const { larghezza, altezza } = FORMATI[formato];
  const titolo = nome || altBadge(famiglia);
  const gruppo = `${famiglia}-${idScheda || 'unico'}`;

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(codice);
      setCopiato(true);
      window.setTimeout(() => setCopiato(false), 2500);
    } catch {
      setCopiato(false);
    }
  };

  const scelta = (attivo: boolean) =>
    `cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium ring-1 transition-colors focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-900 ${
      attivo
        ? 'bg-primary-700 text-white ring-primary-700'
        : 'bg-white text-gray-700 ring-gray-200 hover:ring-primary-300 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700'
    }`;

  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="grid lg:grid-cols-5">
        <div
          className={`lg:col-span-2 flex items-center justify-center p-8 min-h-[14rem] ${
            tema === 'scuro' ? 'bg-gray-900' : 'bg-gray-50 dark:bg-gray-100'
          }`}
        >
          <img src={file} width={larghezza} height={altezza} alt={altBadge(famiglia)} className="max-w-full h-auto" />
        </div>

        <div className="lg:col-span-3 p-5 sm:p-6 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{titolo}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {famiglia === 'associato'
                ? 'Porta al sito di UNASCA Studi.'
                : 'Porta alla scheda del tuo studio sulla mappa della Rete Certificata.'}
            </p>
          </div>

          <div className="flex flex-wrap gap-4">
            <fieldset>
              <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Formato</legend>
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(FORMATI) as FormatoBadge[]).map((f) => (
                  <label key={f} className={scelta(formato === f)}>
                    <input type="radio" name={`formato-${gruppo}`} value={f} checked={formato === f} onChange={() => setFormato(f)} className="sr-only" />
                    {FORMATI[f].etichetta}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Sfondo</legend>
              <div className="flex gap-1.5">
                {TEMI.map((t) => (
                  <label key={t.valore} className={scelta(tema === t.valore)}>
                    <input type="radio" name={`tema-${gruppo}`} value={t.valore} checked={tema === t.valore} onChange={() => setTema(t.valore)} className="sr-only" />
                    {t.etichetta}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <textarea
            readOnly
            aria-label="Codice da incollare nel tuo sito"
            value={codice}
            rows={4}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-300"
          />

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copia}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-700 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-800 transition-colors"
            >
              {copiato ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
              {copiato ? 'Copiato' : 'Copia codice'}
            </button>
            <a
              href={file}
              download
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 hover:ring-primary-300 transition-colors dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700"
            >
              <Download className="w-4 h-4" aria-hidden="true" />
              Scarica immagine
            </a>
            <a
              href={mailtoWebmaster(codice)}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 hover:ring-primary-300 transition-colors dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
              Invia al webmaster
            </a>
          </div>

          <details className="text-sm text-gray-600 dark:text-gray-400">
            <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-300">Preferisci un semplice link di testo?</summary>
            <code className="mt-2 block break-all rounded-lg bg-gray-50 p-3 font-mono text-xs dark:bg-gray-950">
              {codiceLinkTestuale(famiglia, idScheda)}
            </code>
          </details>
        </div>
      </div>
    </section>
  );
};

export default SchedaBadge;
